<template>
  <PageWrapper
    :maxWidth="5000"
    backIcon="arrow-right"
    :handleBack="goToListCenterExecute"
  >
    <template #left-header>
      <Button
        variant="outline-complementary"
        :text="$lang.viewCompetencySummaryScores"
        @click="goToCompetencySummaryScores"
      />
    </template>
    <LoadingWrapper :loading="isLoading">
      <template #loading>
        <Loading :count="5" />
      </template>
      <template #default>
        <b-row class="margin-0">
          <b-col cols="12" class="padding-0">
            <div
              class="overflow-x-auto background-surface margin-top-24 border--solid border--complementary-50 border-radius-8 padding-16"
            >
              <div
                class="min-width-1200 width--max-content table-grid-row font-size-12 color-complementary-300 margin-bottom-8 min-width-1200"
                :style="`gap: 8px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthLabels};`"
              >
                <div
                  v-for="(label, index) in labels"
                  :key="`a-${index}`"
                  class="height-64 flex-start padding-x-16 background-light-gray border-radius-top-8"
                  :class="
                    !index
                      ? 'background-light-gray'
                      : 'background-double-light-gray'
                  "
                >
                  <span
                    class="font-size-16 color-complementary-400 text-ellipsis--line-2 text-center"
                  >
                    {{ label }}
                  </span>
                </div>
              </div>
              <div class="min-width-1200 width--max-content">
                <div
                  v-for="(value, index) in values"
                  :key="`b-${index}`"
                  class="table-grid-row margin-bottom-8 last-child-border-radius-left-4 border--solid border--complementary-50 border-radius-8 padding-16 width-percent-100"
                  :style="`gap: 20px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthValues};`"
                >
                  <div
                    class="font-size-14 color-complementary-300 border--solid border--complementary-50 border--left border--width-2"
                  >
                    <AvatarGroup
                      :data="value.candidate"
                      :hasType="false"
                      :hasProfilePopup="false"
                    />
                  </div>
                  <div
                    v-for="(
                      {
                        id,
                        statusId,
                        statusCaption,
                        isAbsent,
                        evidenceErrorCount,
                      },
                      caseIndex
                    ) in value.cases"
                    :key="`case-${caseIndex}`"
                    class="font-size-14 color-complementary-300 text-center flex-center cursor--pointer"
                    @click="handleOpenChangeStatusModal(id, statusId)"
                  >
                    <ErrorCount
                      :visible="!!evidenceErrorCount"
                      :count="evidenceErrorCount"
                      rowClass="margin-left-8"
                    />
                    <span
                      v-if="isAbsent"
                      class="chips background-error-50 color-error"
                    >
                      {{ $lang.notPresentInExercise }}
                    </span>

                    <span
                      v-else
                      :class="`chips ${generateStatusClass(statusId)}`"
                    >
                      {{ statusCaption }}
                    </span>
                  </div>
                  <div
                    v-show="isAllowWashup(value.cases)"
                    class="color-complementary-200 text-center"
                    @click="goToEditWashup(value.candidate.candidateId)"
                  >
                    <span class="icon-hover">
                      <mdicon name="pencil-outline" size="24" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>
    </LoadingWrapper>
    <Modal
      :show="isChangeStatusModal"
      :title="$lang.changeParvandehStatus"
      :closeOnBackdrop="false"
      :hasFooter="false"
      :hasClose="true"
      :okButton="false"
      @close="handleCloseChangeStatusModal"
    >
      <template #body>
        <ChangeStatus
          :currentCaseStatusId="currentCaseStatusId"
          :currentUserExecutiveRoleId="userExecutiveRoleId"
          :allowSteps="allowStatusSteps"
          :handleSubmit="handleSubmitChangeStatus"
        />
      </template>
    </Modal>
  </PageWrapper>
</template>

<script>
import {
  PageWrapper,
  AvatarGroup,
  Modal,
  ErrorCount,
  LoadingWrapper,
} from "@/components/re-design";
import { Button } from "@/components/re-design/forms";
import Loading from "./loading/Loading.vue";
import ChangeStatus from "./components/ChangeStatus.vue";
import { computed, ref } from "vue";
import { pathOr, includes } from "ramda";
import {
  generateCurrentRoute,
  generateError,
  generateLang,
  goToRoute,
  setUniqueArrayOfObjects,
  toastAlert,
} from "@/shared/utils";
import {
  getWashup,
  setCenterExecuteCaseStatus,
} from "@/modules/kanoon/services";
export default {
  components: {
    PageWrapper,
    AvatarGroup,
    Modal,
    ErrorCount,
    Button,
    ChangeStatus,
    LoadingWrapper,
    Loading,
  },
  setup() {
    const generateLabels = (labels) => {
      const centerExecuteCaseItems = labels
        .map((item) => pathOr([], ["center_execute_case"], item))
        .flat()
        .map((row) => pathOr(null, ["exercise"], row));
      const uniqueItems = setUniqueArrayOfObjects(centerExecuteCaseItems, "id");
      const candidates = generateLang(["candidates"]);
      const exercisesConclusion = generateLang(["exercisesConclusion"]);
      return [
        candidates,
        ...uniqueItems.map(({ caption }) => caption),
        exercisesConclusion,
      ];
    };
    const generateCandidateData = (item) => {
      const candidate = pathOr(null, ["candidate"], item);
      const candidateName =
        pathOr("", ["person", "full_name"], candidate) || "**********";
      const candidateNumber = pathOr(
        "",
        ["person_center_execute_code"],
        candidate
      );
      return {
        candidateId: pathOr(null, ["id"], candidate),
        type: generateLang(["candidate"]),
        id: candidateNumber,
        name: candidateName,
        images: [pathOr("", ["person", "img_thumb"], candidate)],
      };
    };
    const generateCases = (item) => {
      const casesArr = pathOr([], ["center_execute_case"], item);
      const cases = casesArr.map((caseIem) => {
        return {
          id: pathOr(null, ["id"], caseIem),
          isAbsent: pathOr(false, ["is_absent"], caseIem),
          allowWashup: pathOr(false, ["allow_washup"], caseIem),
          evidenceErrorCount: pathOr(0, ["evidence_error_count"], caseIem),
          statusCaption: pathOr("", ["status", "caption"], caseIem),
          statusId: pathOr(null, ["status", "id"], caseIem),
          exerciseCaption: pathOr("", ["exercise", "caption"], caseIem),
        };
      });
      return cases;
    };
    const generateValues = (items) => {
      return items.map((item) => {
        const candidate = generateCandidateData(item);
        const cases = generateCases(item);
        return { candidate, cases };
      });
    };

    // get washup
    const centerExecuteId = generateCurrentRoute(["params", "centerExecuteId"]);
    const centerExecuteStatusId = ref(null);
    const userExecutiveRoleId = ref(null);
    const labels = ref([]);
    const values = ref([]);
    const isLoading = ref(false);
    const getWashUps = async () => {
      try {
        isLoading.value = true;
        const response = await getWashup(centerExecuteId);
        const { statusId, executiveRoleId, items } = response;
        centerExecuteStatusId.value = statusId;
        userExecutiveRoleId.value = executiveRoleId;
        labels.value = generateLabels(items);
        values.value = generateValues(items);
      } catch (error) {
        generateError(error);
      } finally {
        isLoading.value = false;
      }
    };
    getWashUps();

    // generate table columns with
    const generateColumnWidthLabels = computed(() =>
      labels.value
        .map((item, index) => {
          const lastItem = labels.value.length - 1 === index;
          return lastItem ? "170px" : "250px";
        })
        .join(" ")
    );
    const generateColumnWidthValues = computed(() =>
      labels.value
        .map((item, index) => {
          const lastItem = labels.value.length - 1 === index;
          return lastItem ? "130px" : "238px";
        })
        .join(" ")
    );

    // generate status color classes
    const generateStatusClass = (id) => {
      const colors = {
        1: "background-success-50 color-success",
        2: "background-success-50 color-success",
        3: "background-success-50 color-success",
        4: "background-success-50 color-success",
        5: "background-success-50 color-success",
        6: "background-success-50 color-success",
        7: "background-warning-50 color-warning",
        8: "background-warning-50 color-warning",
      };
      return pathOr("", [id], colors);
    };

    // handle conditions for show open washup button
    const isAllowWashup = (cases) => {
      return cases.some(({ allowWashup, statusId }) => {
        let allowCaseStatusIdArr = [];

        // مدیر کانون یا واشاپر
        if (includes(userExecutiveRoleId.value, [3, 5])) {
          allowCaseStatusIdArr = [4];
        }

        // ویراستار
        if (includes(userExecutiveRoleId.value, [7])) {
          allowCaseStatusIdArr = [5];
        }

        // is null center execute role
        if (includes(userExecutiveRoleId.value, [null])) {
          allowCaseStatusIdArr = [4, 5, 6];
        }

        // check allow case statuses
        const isAllowCaseStatus = includes(statusId, allowCaseStatusIdArr);

        return allowWashup && isAllowCaseStatus;
      });
    };

    // go to competency summary page
    const goToCompetencySummaryScores = () => {
      const name = "washup-competency-overview";
      goToRoute(name, { centerExecuteId }, {}, false);
    };

    // go to edit washup page
    const goToEditWashup = (candidateId) => {
      const name = "edit-washup";
      goToRoute(name, { centerExecuteId, candidateId }, {}, false);
    };

    // handle change status
    const allowStatusSteps = [
      {
        id: 3,
        caption: "ارسال به مدیر کانون",
        allowStatuses: [4],
        allowUserExecutiveRoles: [3, 5],
      },
      {
        id: 4,
        caption: "جمع بندی",
        allowStatuses: [5],
        allowUserExecutiveRoles: [3, 5],
      },
      {
        id: 5,
        caption: "پایان فرایند ارزیابی",
        allowStatuses: [4],
        allowUserExecutiveRoles: [3, 5],
      },
      {
        id: 5,
        caption: "پایان فرایند ارزیابی",
        allowStatuses: [6],
        allowUserExecutiveRoles: [7],
      },
      {
        id: 6,
        caption: "تایید ویراستار",
        allowStatuses: [5],
        allowUserExecutiveRoles: [7],
      },
    ];
    const currentCaseId = ref(null);
    const currentCaseStatusId = ref(null);
    const isChangeStatusModal = ref(false);
    const handleOpenChangeStatusModal = (caseId, caseStatusId) => {
      currentCaseId.value = caseId;
      currentCaseStatusId.value = caseStatusId;
      const isAllowExecuteRole = includes(userExecutiveRoleId.value, [3, 5, 7]);
      const isAllowStatuses = includes(caseStatusId, [4, 5, 6]);
      if (!isAllowExecuteRole || !isAllowStatuses) {
        return toastAlert(
          generateLang(["washupDontAllowChangeStatusAlert"]),
          "warning"
        );
      }
      isChangeStatusModal.value = true;
    };
    const handleCloseChangeStatusModal = () => {
      isChangeStatusModal.value = false;
    };
    const handleSubmitChangeStatus = async (caseStatusId) => {
      try {
        const response = await setCenterExecuteCaseStatus(
          centerExecuteId,
          currentCaseId.value,
          caseStatusId
        );
        const { message } = response;
        toastAlert(message);
        handleCloseChangeStatusModal();
        getWashUps();
      } catch (error) {
        generateError(error);
      }
    };

    const goToListCenterExecute = () => {
      const centerExecuteListBackRoute = localStorage.getItem(
        "centerExecuteListBackRoute"
      );
      goToRoute(centerExecuteListBackRoute, {}, {}, false);
    };

    return {
      labels,
      values,
      generateColumnWidthLabels,
      generateColumnWidthValues,
      generateStatusClass,
      isAllowWashup,
      goToCompetencySummaryScores,
      allowStatusSteps,
      isChangeStatusModal,
      handleOpenChangeStatusModal,
      handleCloseChangeStatusModal,
      handleSubmitChangeStatus,
      currentCaseStatusId,
      userExecutiveRoleId,
      goToEditWashup,
      goToListCenterExecute,
      isLoading,
    };
  },
};
</script>
<style lang="scss">
.table-grid-row {
  display: grid;
  grid-template-rows: auto;
  align-items: start;
}

.border-radius-left-4 {
  border-radius: 4px 0 0 4px;
}
.last-child-border-radius-left-4 {
  span:last-child {
    @extend .border-radius-left-4;
  }
}
</style>
